import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost' | 'light';
export type ButtonSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-button',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './button.component.html',
  styleUrl: './button.component.css'
})
export class ButtonComponent {
  @Input() variant: ButtonVariant = 'primary';
  @Input() size: ButtonSize = 'md';
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() loading: boolean = false;
  @Input() disabled: boolean = false;
  @Input() fullWidth: boolean = false;
  @Input() routerLink?: any[] | string;
  @Input() queryParamsHandling?: 'merge' | 'preserve' | '' = '';
  @Input() queryParams?: Record<string, any>;
  @Input() customClass: string = '';
  @Input() ariaLabel?: string;

  @Output() btnClick = new EventEmitter<MouseEvent>();

  onClick(event: MouseEvent): void {
    if (this.disabled || this.loading) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.btnClick.emit(event);
  }

  get buttonClasses(): string {
    const classes: string[] = [
      'button',
      'inline-flex items-center justify-center gap-2 select-none font-medium'
    ];

    // Variantes issues de _button.scss
    switch (this.variant) {
      case 'secondary':
        classes.push('button--secondary');
        break;
      case 'outline':
        classes.push('button--outline');
        break;
      case 'danger':
        classes.push('bg-red-600 hover:bg-red-700 text-white border-0');
        break;
      case 'ghost':
        classes.push('bg-transparent text-slate-700 hover:bg-slate-100 border-0');
        break;
      case 'light':
        classes.push('bg-secondary-50 text-secondary-700 hover:bg-secondary-100 border border-secondary-200');
        break;
      // 'primary' utilise les styles natifs de .button définis dans _button.scss
    }

    // Tailles
    if (this.size === 'sm') {
      classes.push('!px-3 !py-1.5 text-xs');
    } else if (this.size === 'lg') {
      classes.push('!px-6 !py-2.5 text-base');
    }

    if (this.fullWidth) {
      classes.push('w-full');
    }

    if (this.disabled || this.loading) {
      classes.push('button--disabled opacity-60');
    }

    if (this.customClass) {
      classes.push(this.customClass);
    }

    return classes.join(' ').trim();
  }

  get spinnerColorClass(): string {
    if (this.variant === 'secondary' || this.variant === 'outline' || this.variant === 'ghost') {
      return 'text-primary';
    }
    return 'text-white';
  }
}
